import posthog from 'posthog-js';
import { Store } from '../common/configureStore';

// Canonical PostHog event names shared across every Brisque client site.
// Call sites must import from here and never call posthog.capture directly —
// that's what keeps event names/props consistent across restaurants.
export const ANALYTICS_EVENTS = {
  HOME_PAGE_VIEWED: 'home_page_viewed',
  ORDER_ONLINE_CLICKED: 'order_online_clicked',
  ITEM_ADDED_TO_CART: 'item_added_to_cart',
  CART_VIEWED: 'cart_viewed',
  CHECKOUT_STARTED: 'checkout_started',
  TIP_DIALOG_VIEWED: 'tip_dialog_viewed',
  TIP_SELECTED: 'tip_selected',
  CHECKOUT_PAGE_VIEWED: 'checkout_page_viewed',
  PAYMENT_PAGE_VIEWED: 'payment_page_viewed',
  PAYMENT_INITIATED: 'payment_initiated',
  ORDER_COMPLETED: 'order_completed',
  PAYMENT_COMPLETED: 'payment_completed',
  PAYMENT_FAILED: 'payment_failed',
  ORDER_TRACKING_VIEWED: 'order_tracking_viewed',
  DELIVERY_PAGE_VIEWED: 'delivery_page_viewed',
  DELIVERY_ORDER_PLACED: 'delivery_order_placed',
  DELIVERY_SLOT_VIEWED: 'delivery_slot_viewed',
  DELIVERY_SLOT_SELECTED: 'delivery_slot_selected',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

// This app serves many restaurants dynamically from one deployment (:merchantSlug
// routing), unlike the single-merchant-per-build Next.js client sites — so
// merchant_name can't be a build-time env var here. It's resolved at call time
// from whichever restaurant is currently loaded in redux.
const UNCONFIGURED_MERCHANT_NAME = 'UNCONFIGURED_MERCHANT_NAME';
let hasWarnedMissingMerchantName = false;

function getMerchantName(): string {
  const state: any = Store.getState();
  const branchName = state?.restaurant?.currentRestaurantDetail?.branchName;
  const merchantSlug = state?.restaurant?.merchantSlug;

  if (branchName) return branchName;
  if (merchantSlug) return merchantSlug;

  if (!hasWarnedMissingMerchantName) {
    hasWarnedMissingMerchantName = true;
    // eslint-disable-next-line no-console
    console.error(
      '[analytics] Could not resolve a merchant name from redux state ' +
        '(state.restaurant.currentRestaurantDetail.branchName / state.restaurant.merchantSlug ' +
        `are both empty). Events will be tagged "${UNCONFIGURED_MERCHANT_NAME}" until restaurant data loads — ` +
        'this must not ship to production traffic unfixed.'
    );
  }
  return UNCONFIGURED_MERCHANT_NAME;
}

const VALID_ORDER_TYPES = new Set(['Pickup', 'Delivery']);

// Normalizes redux's order-type values (e.g. currentMenuType: 'Pickup'|'Delivery',
// currentOrderType: 'pickup'|'delivery') to the exact casing analytics requires.
// Returns undefined for anything else (e.g. dine-in/table orders), which callers
// should simply omit rather than invent a third value.
export function normalizeOrderType(value: unknown): 'Pickup' | 'Delivery' | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'pickup') return 'Pickup';
  if (normalized === 'delivery') return 'Delivery';
  return undefined;
}

export function trackEvent(eventName: AnalyticsEventName, properties: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;

  // Call sites include this on the critical path (order placement, navigation,
  // date/slot selection) — analytics must never be able to throw and interrupt
  // that surrounding business logic, so every bit of work here is guarded.
  try {
    if (
      process.env.NODE_ENV !== 'production' &&
      'order_type' in properties &&
      properties.order_type !== undefined &&
      !VALID_ORDER_TYPES.has(properties.order_type as string)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `[analytics] "${eventName}" was called with order_type="${properties.order_type}" — ` +
          'expected exactly "Pickup" or "Delivery".'
      );
    }

    posthog.capture(eventName, {
      ...properties,
      merchant_name: getMerchantName(),
      feature: 'online_order',
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[analytics] trackEvent("${eventName}") failed — swallowed to protect the caller.`, err);
  }
}

// Per-event Set of order ids already tracked, so a completion event (fired from
// a sagaCallBack, not a component effect) can never double-count the same order
// if the callback plumbing ever invokes it more than once (retry, duplicate
// dispatch, etc). Module-level and keyed by event name so ORDER_COMPLETED and
// PAYMENT_COMPLETED for the same order id are tracked independently.
const trackedOrderEventIds = new Map<AnalyticsEventName, Set<string>>();

export function trackEventOncePerOrder(
  eventName: AnalyticsEventName,
  orderId: string | number | undefined | null,
  properties: Record<string, unknown> = {}
): void {
  if (!orderId) {
    trackEvent(eventName, properties);
    return;
  }
  const key = String(orderId);
  let seen = trackedOrderEventIds.get(eventName);
  if (!seen) {
    seen = new Set();
    trackedOrderEventIds.set(eventName, seen);
  }
  if (seen.has(key)) return;
  seen.add(key);
  trackEvent(eventName, properties);
}
